/**
 * CrunchAlpha Shared UI Components
 * Single source of truth — import dari sini, jangan define ulang di page lain
 */

// ── NUMBER FORMAT ─────────────────────────────────────────────────────────────
export const fmt2   = n => Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
export const fmtPct = (n,d=2) => `${Number(n||0).toFixed(d)}%`;
export const fmtDollar = n => (Number(n||0)>=0?"+$":"-$")+fmt2(Math.abs(Number(n||0)));
export const fmtPts = n => {
  const v = Math.abs(Number(n||0));
  const s = Number(n||0) < 0 ? "-" : "";
  if(v>=1e9) return s+(v/1e9).toFixed(2)+"B pts";
  if(v>=1e6) return s+(v/1e6).toFixed(2)+"M pts";
  if(v>=1e3) return s+(v/1e3).toFixed(1)+"K pts";
  return s+v.toFixed(0)+" pts";
};
export const fmtK   = n => {
  const v=Math.abs(Number(n||0)), s=Number(n||0)<0?"-":"";
  return v>=1e6?s+"$"+(v/1e6).toFixed(2)+"M":v>=1e3?s+"$"+(v/1e3).toFixed(2)+"K":s+"$"+v.toFixed(2);
};

// ── COLOR HELPERS ─────────────────────────────────────────────────────────────
export const clrPnl = n => Number(n||0)>0?"var(--success)":Number(n||0)<0?"var(--danger)":"var(--text-muted)";
export const clrDD  = n => { const p=Number(n||0); return p>=100?"#c0392b":p>40?"var(--danger)":p>20?"var(--warning)":"var(--success)"; };

// ── REUSABLE COMPONENTS ───────────────────────────────────────────────────────

/** BUY/SELL badge — sama di semua table */
export const DirBadge = ({dir}) => (
  <span style={{fontWeight:800,fontSize:11,
    color:["BUY","buy"].includes(dir)?"var(--success)":"var(--danger)"}}>
    {String(dir).toUpperCase()}
  </span>
);

/** P&L cell — +$10.31 / -$10.31 */
export const PnlCell = ({value,style={}}) => (
  <span style={{fontWeight:700,color:clrPnl(value),...style}}>
    {value!=null?(Number(value)>=0?"+$":"-$")+fmt2(Math.abs(value)):"—"}
  </span>
);

/** Status badge — OPENED / CLOSED_TP / REJECTED dll */
const STATUS_MAP = {
  OPENED:         {c:"var(--accent)",   bg:"rgba(78,154,241,0.12)"},
  PENDING:        {c:"var(--warning)",  bg:"rgba(245,158,11,0.12)"},
  ACTIVE:         {c:"var(--success)",  bg:"rgba(34,197,94,0.12)"},
  CLOSED_TP:      {c:"var(--success)",  bg:"rgba(34,197,94,0.12)"},
  CLOSED_SL:      {c:"var(--danger)",   bg:"rgba(239,68,68,0.12)"},
  CLOSED_MANUAL:  {c:"var(--text-muted)",bg:"rgba(255,255,255,0.06)"},
  REJECTED:       {c:"var(--text-muted)",bg:"rgba(255,255,255,0.06)"},
  CANCELLED_MANUAL:{c:"var(--text-muted)",bg:"rgba(255,255,255,0.06)"},
  INACTIVE:       {c:"var(--text-muted)",bg:"rgba(255,255,255,0.06)"},
};
export const StatusBadge = ({status}) => {
  const s=STATUS_MAP[status]||{c:"var(--text-muted)",bg:"rgba(255,255,255,0.06)"};
  return (
    <span style={{fontSize:10,padding:"2px 8px",borderRadius:20,fontWeight:700,
      background:s.bg,color:s.c,whiteSpace:"nowrap",border:`1px solid ${s.c}33`}}>
      {status}
    </span>
  );
};

/** Stat card — SINGLE SOURCE for all summary rows. Per DESIGN_SYSTEM.md:
 * value besar = 22px/700, radius-xl, card label uppercase 10-11px.
 * color prop should ONLY be passed for Net P&L / ROI / Max DD / status —
 * everything else (Balance, Win Rate, Subscriber Count, etc.) omits it
 * and gets the default var(--text-number). */
export const StatCard = ({label,value,color,sub,subColor,center=false}) => (
  <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",
    borderRadius:"var(--radius-xl)",padding:"14px 16px",textAlign:center?"center":"left"}}>
    <div style={{fontSize:11,color:"var(--text-faint)",marginBottom:6,fontWeight:600,
      textTransform:"uppercase",letterSpacing:"0.07em"}}>{label}</div>
    <div style={{fontSize:22,fontWeight:700,color:color||"var(--text-number)",lineHeight:1.1,
      whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{value}</div>
    {sub&&<div style={{fontSize:11,color:subColor||"var(--text-faint)",marginTop:4}}>{sub}</div>}
  </div>
);

/** Table header row */
export const THead = ({cols}) => (
  <thead>
    <tr style={{borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
      {cols.map(h=>(
        <th key={h} style={{padding:"8px 12px",textAlign:"left",fontSize:10,fontWeight:700,
          color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:"0.04em",
          whiteSpace:"nowrap"}}>{h}</th>
      ))}
    </tr>
  </thead>
);

/** Table row hover style */
export const trHover = i => ({
  borderBottom:"1px solid rgba(255,255,255,0.04)",
  background:i%2===0?"transparent":"rgba(255,255,255,0.01)",
  cursor:"default",
});

/** Pagination controls */
export const Pagination = ({page,totalPages,setPage}) => (
  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
    marginTop:14,paddingTop:12,borderTop:"1px solid rgba(255,255,255,0.07)"}}>
    <span style={{fontSize:12,color:"var(--text-muted)"}}>{page} / {totalPages}</span>
    <div style={{display:"flex",gap:6}}>
      <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}
        style={{padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",
          border:"1px solid var(--border)",background:"transparent",
          color:"var(--text-muted)",opacity:page===1?0.4:1}}>← Prev</button>
      <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}
        style={{padding:"5px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",
          border:"1px solid var(--border)",background:"transparent",
          color:"var(--text-muted)",opacity:page===totalPages?0.4:1}}>Next →</button>
    </div>
  </div>
);

/** Empty state */
export const EmptyState = ({icon="",title,subtitle}) => (
  <div style={{textAlign:"center",padding:"48px 24px",color:"var(--text-muted)"}}>
    <div style={{fontSize:32,marginBottom:10}}>{icon}</div>
    <div style={{fontSize:14,fontWeight:700,color:"var(--text-main)",marginBottom:6}}>{title}</div>
    {subtitle&&<div style={{fontSize:12}}>{subtitle}</div>}
  </div>
);

/** Section label with divider */
// ── SMART PROFILE REPORT CARD (additive, Fase 1) ───────────────────────────
// Dipakai di TraderProfile.jsx & AnalystProfile.jsx. smartReport = data.smart_report
// dari GetTraderProfile / GetPublicAnalystProfile. Render null kalau available=false
// (investor belum login / belum punya EA key dengan risk_level terisi).
export const SmartProfileReportCard = ({ smartReport }) => {
  if (!smartReport || !smartReport.available) return null;
  const statusColor = {
    match: "var(--success)",
    caution: "var(--warning)",
    mismatch: "var(--danger)",
    unknown: "var(--text-faint)",
  };
  const statusLabel = {
    match: "MATCH",
    caution: "CAUTION",
    mismatch: "MISMATCH",
    unknown: "N/A",
  };
  const accounts = smartReport.accounts || [];
  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
        Risk Match by Your Accounts
      </div>
      {accounts.length === 0 ? (
        <div style={{ fontSize: 12, color: "var(--text-faint)" }}>{smartReport.note}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {accounts.map(a => {
            const c = statusColor[a.match_status] || statusColor.unknown;
            return (
              <div key={a.account_id} style={{
                display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
                padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
              }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text-main)" }}>{a.account_label}</span>
                  <span style={{ fontSize: 11, color: "var(--text-faint)" }}>{a.match_reason}</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }}>
                  <span style={{
                    fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 4,
                    background: `${c}18`, color: c, border: `1px solid ${c}44`,
                    textTransform: "uppercase", letterSpacing: "0.04em",
                  }}>{statusLabel[a.match_status] || a.match_status}</span>
                  <span style={{ fontSize: 10, color: "var(--text-faint)", textTransform: "capitalize" }}>{a.risk_level}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {accounts.length > 0 && smartReport.note && (
        <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 8, lineHeight: 1.5, fontStyle: "italic" }}>
          {smartReport.note}
        </div>
      )}
      {smartReport.risk_basis === "drawdown_based" && (
        <div style={{ fontSize: 10, color: "var(--text-faint)", fontStyle: "italic", marginTop: 4 }}>
          Based on historical drawdown (signal-only strategy)
        </div>
      )}
    </div>
  );
};

export const SectionLabel = ({children,count,right}) => (
  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
    <span style={{fontSize:10,fontWeight:700,color:"var(--text-muted)",
      textTransform:"uppercase",letterSpacing:"0.08em"}}>{children}</span>
    {count!==undefined&&
      <span style={{fontSize:10,padding:"1px 6px",borderRadius:20,
        background:"rgba(255,255,255,0.06)",color:"var(--text-muted)",
        border:"0.5px solid rgba(255,255,255,0.08)"}}>{count}</span>}
    <div style={{flex:1,height:"0.5px",background:"rgba(255,255,255,0.07)"}}/>
    {right}
  </div>
);
